<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCompanyContactsStore } from 'dashboard/stores/companyContacts';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  companyId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const companyContactsStore = useCompanyContactsStore();

const showAddForm = ref(false);
const newContactId = ref('');
const newContactRole = ref('');
const newContactIsPrimary = ref(false);
const editingContactId = ref(null);
const editingRole = ref('');
const editingIsPrimary = ref(false);

const contacts = computed(() => companyContactsStore.getContacts);
const uiFlags = computed(() => companyContactsStore.getUIFlags);
const primaryContact = computed(() => companyContactsStore.getPrimaryContact);

const isFetching = computed(() => uiFlags.value.fetchingList);
const isCreating = computed(() => uiFlags.value.creating);

const fetchContacts = async () => {
  await companyContactsStore.get(props.companyId);
};

const handleCreate = async () => {
  if (!newContactId.value) return;
  
  try {
    await companyContactsStore.create(props.companyId, {
      contactId: parseInt(newContactId.value),
      role: newContactRole.value,
      isPrimary: newContactIsPrimary.value
    });
    resetForm();
    useAlert(t('COMPANIES.CONTACTS.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.CONTACTS.CREATE_ERROR'));
  }
};

const resetForm = () => {
  showAddForm.value = false;
  newContactId.value = '';
  newContactRole.value = '';
  newContactIsPrimary.value = false;
};

const startEditing = (contact) => {
  editingContactId.value = contact.id;
  editingRole.value = contact.role || '';
  editingIsPrimary.value = contact.isPrimary || false;
};

const cancelEditing = () => {
  editingContactId.value = null;
  editingRole.value = '';
  editingIsPrimary.value = false;
};

const handleUpdate = async (companyContactId) => {
  try {
    await companyContactsStore.update(props.companyId, companyContactId, {
      role: editingRole.value,
      isPrimary: editingIsPrimary.value
    });
    editingContactId.value = null;
    useAlert(t('COMPANIES.CONTACTS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.CONTACTS.UPDATE_ERROR'));
  }
};

const handleDelete = async (companyContactId) => {
  if (!confirm(t('COMPANIES.CONTACTS.DELETE_CONFIRM'))) return;
  
  try {
    await companyContactsStore.delete(props.companyId, companyContactId);
    useAlert(t('COMPANIES.CONTACTS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.CONTACTS.DELETE_ERROR'));
  }
};

</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header with Add Button -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('COMPANIES.CONTACTS.TITLE') }}
        <span v-if="contacts.length" class="text-n-slate-11 ml-1">({{ contacts.length }})</span>
      </h3>
      <button
        v-if="!showAddForm"
        class="text-xs text-n-brand hover:text-n-brand-dark font-medium"
        @click="showAddForm = true"
      >
        {{ t('COMPANIES.CONTACTS.ADD') }}
      </button>
    </div>

    <!-- Add Contact Form -->
    <div v-if="showAddForm" class="p-4 border-b border-n-weak bg-n-alpha-black2">
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs text-n-slate-11">Contact ID</label>
          <Input
            v-model="newContactId"
            :placeholder="t('COMPANIES.CONTACTS.CONTACT_ID_PLACEHOLDER')"
            type="number"
            size="sm"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs text-n-slate-11">Role</label>
          <Input
            v-model="newContactRole"
            :placeholder="t('COMPANIES.CONTACTS.ROLE_PLACEHOLDER')"
            size="sm"
          />
        </div>
        <label class="flex items-center gap-2 text-sm text-n-slate-11 cursor-pointer">
          <input
            v-model="newContactIsPrimary"
            type="checkbox"
            class="w-3.5 h-3.5 rounded border-n-weak text-n-brand"
          />
          <span>{{ t('COMPANIES.CONTACTS.IS_PRIMARY') }}</span>
        </label>
        <div class="flex items-center gap-3 mt-1">
          <button
            class="text-xs text-n-slate-11 hover:text-n-slate-12"
            @click="resetForm"
          >
            Cancel
          </button>
          <button
            class="text-xs text-n-brand font-medium"
            :disabled="!newContactId || isCreating"
            @click="handleCreate"
          >
            Add
          </button>
        </div>
      </div>
    </div>

    <!-- Contacts List -->
    <div class="flex-1 overflow-y-auto p-4">
      <div v-if="isFetching" class="flex items-center justify-center py-8">
        <Spinner />
      </div>
      
      <div v-else-if="contacts.length === 0" class="text-center py-8 text-n-slate-11">
        {{ t('COMPANIES.CONTACTS.EMPTY') }}
      </div>
      
      <div v-else class="flex flex-col gap-3">
        <div
          v-for="companyContact in contacts"
          :key="companyContact.id"
          class="bg-n-alpha-black2 rounded-lg p-3 group relative"
        >
          <!-- View Mode -->
          <div v-if="editingContactId !== companyContact.id" class="flex items-start gap-3">
            <Avatar
              :username="companyContact.contact?.name"
              :src="companyContact.contact?.avatarUrl"
              :size="40"
              hide-offline-status
              rounded-full
            />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ companyContact.contact?.name || t('COMPANIES.CONTACTS.UNNAMED') }}
                </span>
                <span
                  v-if="companyContact.isPrimary"
                  class="px-2 py-0.5 text-xs bg-n-brand/10 text-n-brand rounded-full"
                >
                  {{ t('COMPANIES.CONTACTS.PRIMARY') }}
                </span>
              </div>
              <div v-if="companyContact.role" class="text-xs text-n-slate-11 mt-0.5">
                {{ companyContact.role }}
              </div>
              <div v-if="companyContact.contact?.email" class="text-xs text-n-slate-11 truncate mt-0.5">
                {{ companyContact.contact.email }}
              </div>
            </div>
            
            <!-- Actions -->
            <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="xs"
                icon="i-lucide-pencil"
                @click="startEditing(companyContact)"
              />
              <Button
                variant="ghost"
                size="xs"
                color="ruby"
                icon="i-lucide-trash-2"
                @click="handleDelete(companyContact.id)"
              />
            </div>
          </div>
          
          <!-- Edit Mode -->
          <div v-else class="mt-3 pt-3 border-t border-n-strong">
            <div class="flex items-start gap-3">
              <Avatar
                :username="companyContact.contact?.name"
                :src="companyContact.contact?.avatarUrl"
                :size="32"
                hide-offline-status
                rounded-full
              />
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-n-slate-12 mb-2">
                  {{ companyContact.contact?.name }}
                </div>
                <div class="flex flex-col gap-2">
                  <label class="text-xs text-n-slate-11">Role</label>
                  <Input
                    v-model="editingRole"
                    :placeholder="t('COMPANIES.CONTACTS.ROLE_PLACEHOLDER')"
                    size="sm"
                  />
                  <label class="flex items-center gap-2 text-sm text-n-slate-11 cursor-pointer">
                    <input
                      v-model="editingIsPrimary"
                      type="checkbox"
                      class="w-3.5 h-3.5 rounded border-n-weak text-n-brand"
                    />
                    <span>{{ t('COMPANIES.CONTACTS.IS_PRIMARY') }}</span>
                  </label>
                  <div class="flex items-center gap-3 mt-1">
                    <button
                      class="text-xs text-n-slate-11 hover:text-n-slate-12"
                      @click="cancelEditing"
                    >
                      Cancel
                    </button>
                    <button
                      class="text-xs text-n-brand font-medium"
                      @click="handleUpdate(companyContact.id)"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
